import React, { useState, Component } from 'react';
/**
 * Window Component
 * macOS-style window with full window management
 */

import { motion, useDragControls, PanInfo } from 'framer-motion';
import { X, Minus, Maximize2, Minimize2 } from 'lucide-react';
import { useTheme } from '../../core/contexts/ThemeContext';
import { useWindows } from '../../core/contexts/WindowContext';
import { WindowState } from '../../core/types/window';
interface WindowProps {
  window: WindowState;
  children: React.ReactNode;
}
export function Window({
  window,
  children
}: WindowProps) {
  const {
    getThemeClasses,
    t
  } = useTheme();
  const {
    windows,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    focusWindow,
    updateWindowPosition,
    updateWindowSize,
    isMobile
  } = useWindows();
  const theme = getThemeClasses();
  const dragControls = useDragControls();
  const [isResizing, setIsResizing] = useState(false);
  // Check if this window is active (highest zIndex)
  const maxZIndex = Math.max(...windows.map(w => w.zIndex));
  const isActive = window.zIndex === maxZIndex;
  // Mobile: fullscreen overlay
  if (isMobile) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: window.isMinimized ? 0 : 1,
      scale: window.isMinimized ? 0.95 : 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} transition={{
      duration: 0.2
    }} style={{
      display: window.isMinimized ? 'none' : 'flex',
      position: 'fixed',
      inset: 0,
      zIndex: window.zIndex,
      flexDirection: 'column'
    }} className={`${theme.bg} ${theme.text}`} data-id="element-952">
        <div className={`${theme.card} px-4 py-3 flex items-center justify-between border-b`} data-id="element-953">
          <h2 className="font-semibold" data-id="element-954">{window.title}</h2>
          <button onClick={() => closeWindow(window.id)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-id="element-955">
            <X className="w-5 h-5" data-id="element-956" />
          </button>
        </div>
        <div className="flex-1 overflow-auto" data-id="element-957">{children}</div>
      </motion.div>;
  }
  // Desktop: draggable, resizable window
  const handleDragEnd = (_: any, info: PanInfo) => {
    if (!window.isMaximized) {
      updateWindowPosition(window.id, {
        x: window.position.x + info.offset.x,
        y: window.position.y + info.offset.y
      });
    }
  };
  return <motion.div drag={!window.isMaximized && !isResizing} dragControls={dragControls} dragListener={false} dragMomentum={false} onDragEnd={handleDragEnd} initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: window.isMinimized ? 0 : isActive ? 1 : 0.95,
    scale: window.isMinimized ? 0.9 : 1,
    x: window.isMaximized ? 0 : window.position.x,
    y: window.isMaximized ? 0 : window.position.y,
    width: window.isMaximized ? '100vw' : window.size.width,
    height: window.isMaximized ? '100vh' : window.size.height,
    filter: isActive ? 'none' : 'brightness(0.9)'
  }} exit={{
    opacity: 0,
    scale: 0.9
  }} transition={{
    duration: 0.2,
    ease: 'easeOut'
  }} style={{
    zIndex: window.zIndex,
    position: 'fixed',
    top: 0,
    left: 0,
    pointerEvents: window.isMinimized ? 'none' : 'auto'
  }} onClick={() => !isActive && focusWindow(window.id)} className={`
        ${theme.card} ${theme.shadow}
        rounded-xl overflow-hidden
        ${isActive ? 'ring-2 ring-fuchsia-500/30 shadow-[0_0_30px_rgba(236,72,153,0.2)]' : 'ring-1 ring-white/10'}
        transition-shadow duration-200
      `} data-id="element-958">
      {/* Window Header */}
      <div onPointerDown={e => {
      if (!window.isMaximized) {
        dragControls.start(e);
      }
    }} className={`
          px-4 py-3 flex items-center justify-between
          select-none
          ${theme.card} border-b ${theme.text}
          ${window.isMaximized ? 'cursor-default' : 'cursor-move'}
        `} data-id="element-959">
        {/* Traffic Lights */}
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()} data-id="element-960">
          <button onClick={() => closeWindow(window.id)} className="w-3 h-3 rounded-full bg-gradient-to-br from-red-400 to-red-600 hover:from-red-500 hover:to-red-700 transition-all shadow-sm group relative" title={t('actions.close')} data-id="element-961">
            <X className="w-2 h-2 text-red-900 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-962" />
          </button>
          <button onClick={() => minimizeWindow(window.id)} className="w-3 h-3 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 transition-all shadow-sm group relative" title={t('actions.minimize')} data-id="element-963">
            <Minus className="w-2 h-2 text-yellow-900 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-964" />
          </button>
          <button onClick={() => maximizeWindow(window.id)} className="w-3 h-3 rounded-full bg-gradient-to-br from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 transition-all shadow-sm group relative" title={window.isMaximized ? t('actions.restore') : t('actions.maximize')} data-id="element-965">
            {window.isMaximized ? <Minimize2 className="w-2 h-2 text-green-900 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-966" /> : <Maximize2 className="w-2 h-2 text-green-900 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-967" />}
          </button>
        </div>
        {/* Title */}
        <div className="absolute left-1/2 -translate-x-1/2 font-medium text-sm pointer-events-none" data-id="element-968">
          {window.title}
        </div>
      </div>
      {/* Window Content */}
      <div className="h-[calc(100%-48px)] overflow-auto" data-id="element-969">{children}</div>
      {/* Resize Handles (only when not maximized) */}
      {!window.isMaximized && <>
          {/* Corner Handles */}
          <div className="absolute top-0 left-0 w-4 h-4 cursor-nw-resize z-10" onPointerDown={e => {
        e.stopPropagation();
        setIsResizing(true);
      }} onPointerUp={() => setIsResizing(false)} data-id="element-970" />
          <div className="absolute top-0 right-0 w-4 h-4 cursor-ne-resize z-10" onPointerDown={e => {
        e.stopPropagation();
        setIsResizing(true);
      }} onPointerUp={() => setIsResizing(false)} data-id="element-971" />
          <div className="absolute bottom-0 left-0 w-4 h-4 cursor-sw-resize z-10" onPointerDown={e => {
        e.stopPropagation();
        setIsResizing(true);
      }} onPointerUp={() => setIsResizing(false)} data-id="element-972" />
          <div className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize z-10" onPointerDown={e => {
        e.stopPropagation();
        setIsResizing(true);
      }} onPointerUp={() => setIsResizing(false)} data-id="element-973" />
          {/* Edge Handles */}
          <div className="absolute top-0 left-4 right-4 h-2 cursor-n-resize z-10" onPointerDown={e => {
        e.stopPropagation();
        setIsResizing(true);
      }} onPointerUp={() => setIsResizing(false)} data-id="element-974" />
          <div className="absolute bottom-0 left-4 right-4 h-2 cursor-s-resize z-10" onPointerDown={e => {
        e.stopPropagation();
        setIsResizing(true);
      }} onPointerUp={() => setIsResizing(false)} data-id="element-975" />
          <div className="absolute left-0 top-4 bottom-4 w-2 cursor-w-resize z-10" onPointerDown={e => {
        e.stopPropagation();
        setIsResizing(true);
      }} onPointerUp={() => setIsResizing(false)} data-id="element-976" />
          <div className="absolute right-0 top-4 bottom-4 w-2 cursor-e-resize z-10" onPointerDown={e => {
        e.stopPropagation();
        setIsResizing(true);
      }} onPointerUp={() => setIsResizing(false)} data-id="element-977" />
        </>}
    </motion.div>;
}